import type { WatchSourceType } from '@tsr/shared-types';

/**
 * W4c DUTY WATCH VOCABULARY (coordination-tab council A.3 / #526, ratified 2026-07-03).
 *
 * Every operator-facing string for the per-DESK "Watching / On watch" flag on a file surface.
 * The single source of copy for both surfaces (the incident panel + the request-file header),
 * so the pinned lock (`deskWatchVocabulary.lock.test.ts`) covers the rendered labels.
 *
 * Doctrine (council A.3): watching is DESCRIPTIVE-NO-COLLISION — it names no App A concept. It
 * is NOT command (App A #11), NOT ownership ("Owned" is the signed §4.3 Incident-Command
 * handle), and NOT the Situation Unit monitoring function (App A #76). The distinction is
 * carried by WORD CHOICE ("Watching" / "eyes on"), never by negating a forbidden word — so
 * every string here is clean of own/command/Monitoring and NO scan-exemption is needed
 * (unlike the PEOC-Arrivals framing caption).
 *
 * §F-1 verb LOCKED = "Watching".
 */

/** State — the current desk is watching this file. */
export const WATCHING_STATE_LABEL = 'Watching';
export const ON_WATCH_STATE_LABEL = 'On watch';

/** Action — the toggle affordance. */
export const WATCH_ACTION_LABEL = 'Watch';
export const STOP_WATCH_ACTION_LABEL = 'Stop watching';

/** The WORK object of the watch, per surface. GUARD-3 (FN dignity): the watch verb's object is
 *  always the WORK ("this request"), NEVER a community / party / tenant. */
const WATCH_OBJECT: Record<WatchSourceType, string> = {
  incident: 'this incident',
  assistance_request: 'this request',
  agency_task: 'this task',
};

/** "Watching this request" — the work-scoped state phrase (GUARD-3: object = the WORK). */
export function watchingObjectLabel(sourceType: WatchSourceType): string {
  return `${WATCHING_STATE_LABEL} ${WATCH_OBJECT[sourceType]}`;
}

/** The toggle hover title. Positive phrasing — conveys "eyes-on, not command" by word choice,
 *  never by using the forbidden words. */
export function watchToggleTitle(active: boolean, sourceType: WatchSourceType): string {
  return active
    ? `Stop watching ${WATCH_OBJECT[sourceType]} — remove this desk from the watch`
    : `Watch ${WATCH_OBJECT[sourceType]} — mark this desk eyes-on, without taking the file`;
}

/** "N desks watching" — the cross-desk count badge (singular-aware). Returns null for 0 (no
 *  badge). ⚠ DORMANT-until-#190: the true multi-tenant count needs a #190 broker read; in the
 *  single-seat demo this reads 0/1 (a NAMED SCOPE-OUT). */
export function deskWatchCountLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 desk watching' : `${count} desks watching`;
}

/** "On watch: PEOC" — names the WATCHING DESK (the subject), when a single desk is on watch.
 *  `desk` is a DESK identity (PEOC / Municipal EOC), NEVER the file's originating community —
 *  it is who is watching, not what is watched (GUARD-3 is about the verb's OBJECT). */
export function onWatchByLabel(desk: string): string {
  return `${ON_WATCH_STATE_LABEL}: ${desk}`;
}
